import { UserTypes } from '../../../types';

export type AppStatus = 'on' | 'off' | 'pending';

// "Running" cutoff: pings within this many minutes count as the user actively
// running the app. Tunable; design spec uses ~5m for the active heartbeat.
const RUNNING_THRESHOLD_MIN = 5;

export const deriveAppStatus = (user: UserTypes.Model): AppStatus => {
    if (!user?.appVersion) return 'pending';

    // Prefer the server-computed status when present — it's authoritative
    // and populated on most production users.
    if (user.appStatus === 'ON') return 'on';
    if (user.appStatus === 'OFF') return 'off';

    // Fall back to a recency heuristic on the location ping ONLY. We
    // intentionally don't use user.updatedAt because admin actions
    // (edits, team changes, etc.) bump it, which would surface
    // newly-edited users as falsely "Running".
    const lastPing = user?.mobileLocation?.updatedAt;

    // No server status AND no ping = the user has never had an active
    // session (e.g., backfilled appVersion on a never-installed account).
    // Reserve 'off' for users who previously had a session, otherwise we
    // mislabel "Pending Activation" as "Mobile App: Yes / Closed".
    if (!lastPing && !user.appStatus) return 'pending';
    if (!lastPing) return 'off';

    const minsSince = (Date.now() - new Date(lastPing).getTime()) / 60_000;
    return minsSince < RUNNING_THRESHOLD_MIN ? 'on' : 'off';
};

export const APP_STATUS_META: Record<
    AppStatus,
    { color: string; ring: string; label: string; mobileApp: 'Yes' | 'No' }
> = {
    on: {
        color: '#25b660',
        ring: 'rgba(37,182,96,0.25)',
        label: 'Running',
        mobileApp: 'Yes',
    },
    off: {
        color: '#919EAB',
        ring: 'rgba(145,158,171,0.3)',
        label: 'Closed',
        mobileApp: 'Yes',
    },
    pending: {
        color: '#FFC107',
        ring: 'rgba(255,193,7,0.3)',
        label: 'Not Installed',
        mobileApp: 'No',
    },
};
